import { AuthRepository } from '../../domain/ports/AuthRepository';
import { Admin, createAdmin } from '../../domain/models/Admin';
import { AuthToken, createAuthToken } from '../../domain/models/AuthToken';
import { LoginCredentials } from '../../domain/models/LoginCredentials';
import { HttpClient } from '../../../common/infrastructure/http/HttpClient';
import { AuthenticationError } from '../../../common/domain/errors/AuthenticationError';

/**
 * DTO interfaces for API responses
 */
interface LoginResponseDTO {
  access_token: string;
  token_type: string;
  expires_in: number;
}

interface AdminDTO {
  data: {
    id: string;
    name: string;
    email: string;
    language: string;
    timezone: string;
    createdAt?: string;
    modifiedAt?: string | null;
  };
}

/**
 * HTTP implementation of AuthRepository
 * Communicates with the Symfony backend API
 */
export class HttpAuthRepository implements AuthRepository {
  constructor(
    private adminHttpClient: HttpClient,
    private clientId: string,
  ) {}

  /**
   * Authenticate with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthToken> {
    try {
      // Step 1: Fetch anonymous token from the admin auth endpoint
      const tokenResponse = await this.adminHttpClient.post<LoginResponseDTO>('/auth/token', null, {
        headers: { Authorization: `Basic ${btoa(this.clientId)}` },
      });
      const anonymousToken = tokenResponse.access_token;

      // Step 2: Perform login with anonymous token
      const response = await this.adminHttpClient.post<LoginResponseDTO>(
        '/auth/login',
        {
          email: credentials.email,
          password: credentials.password,
          grant_type: 'password',
        },
        {
          headers: {
            Authorization: `Bearer ${anonymousToken}`,
          },
        },
      );

      return createAuthToken({
        accessToken: response.access_token,
        tokenType: response.token_type,
        expiresIn: response.expires_in,
      });
    } catch (error) {
      // Transform specific errors
      if (
        error instanceof Error &&
        (error.message.includes('Invalid') || error.message.includes('Unauthorized'))
      ) {
        throw AuthenticationError.invalidCredentials();
      }
      throw error;
    }
  }

  /**
   * Refresh the access token using the refresh token cookie
   */
  async refreshToken(): Promise<AuthToken> {
    try {
      const response = await this.adminHttpClient.post<LoginResponseDTO>('/auth/refresh', {
        grant_type: 'refresh_token',
      });

      return createAuthToken({
        accessToken: response.access_token,
        tokenType: response.token_type,
        expiresIn: response.expires_in,
      });
    } catch (error) {
      if (error instanceof Error && error.message.includes('401')) {
        throw AuthenticationError.sessionExpired();
      }
      throw error;
    }
  }

  /**
   * Log out the current user
   */
  async logout(): Promise<void> {
    try {
      await this.adminHttpClient.post('/logout');
    } catch (error) {
      // Logout errors are non-critical - we can still clear local state
      console.error('Logout request failed:', error);
    }
  }

  /**
   * Get the currently authenticated admin
   */
  async getCurrentAdmin(): Promise<Admin> {
    try {
      const response = await this.adminHttpClient.get<AdminDTO>('/me');

      return createAdmin({
        id: response.data.id,
        name: response.data.name,
        email: response.data.email,
        language: response.data.language,
        timezone: response.data.timezone,
        createdAt: response.data.createdAt ? new Date(response.data.createdAt) : new Date(),
        updatedAt: response.data.modifiedAt ? new Date(response.data.modifiedAt) : new Date(),
      });
    } catch (error) {
      if (error instanceof Error && error.message.includes('401')) {
        throw AuthenticationError.unauthorized();
      }
      throw error;
    }
  }
}
